import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { CustomerAddress } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyAddressForm, fromAddress, toAddressBody } from '../api/mappers';
import {
  useCreateCustomerAddress,
  useUpdateCustomerAddress,
} from '../hooks/use-customer-address-mutations';
import {
  ADDRESS_KNOWN_FIELDS,
  ADDRESS_TEXT_FIELDS,
  customerAddressSchema,
  type CustomerAddressFormInput,
  type CustomerAddressFormOutput,
} from '../schemas/customer-address-schema';

const FULL_WIDTH: ReadonlySet<string> = new Set(['label', 'line1', 'line2']);

const MAX_LENGTH: Record<(typeof ADDRESS_TEXT_FIELDS)[number], number> = {
  label: 60,
  firstName: 120,
  lastName: 120,
  company: 120,
  line1: 200,
  line2: 200,
  city: 120,
  region: 120,
  postalCode: 20,
  countryCode: 2,
  phone: 30,
};

export interface CustomerAddressDialogProps {
  storeId: string;
  customerId: string;
  /** The address to edit; omitted to add one. */
  address?: CustomerAddress | undefined;
  /** Prefills the country of a new address; the first one also becomes both defaults. */
  defaultCountry: string;
  isFirst: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Add or edit one entry of a shopper's address book, for an order taken by phone or email. */
export function CustomerAddressDialog({
  storeId,
  customerId,
  address,
  defaultCountry,
  isFirst,
  open,
  onOpenChange,
}: CustomerAddressDialogProps) {
  const { t } = useTranslation(['customers', 'common']);
  const message = useErrorMessage();
  const create = useCreateCustomerAddress(storeId, customerId);
  const update = useUpdateCustomerAddress(storeId, customerId);
  const pending = create.isPending || update.isPending;
  const form = useForm<CustomerAddressFormInput, unknown, CustomerAddressFormOutput>({
    resolver: useI18nResolver(customerAddressSchema),
    defaultValues: address ? fromAddress(address) : emptyAddressForm(defaultCountry, isFirst),
  });

  const close = () => {
    onOpenChange(false);
    create.reset();
    update.reset();
  };

  const submit = form.handleSubmit(async (values) => {
    const body = toAddressBody(values);
    try {
      if (address) await update.mutateAsync({ addressId: address.id, body });
      else await create.mutateAsync(body);
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: ADDRESS_KNOWN_FIELDS })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {address ? t('customers:addresses.editTitle') : t('customers:addresses.addTitle')}
          </DialogTitle>
          <DialogDescription>{t('customers:addresses.dialogDescription')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <div className="grid gap-4 sm:grid-cols-2">
              {ADDRESS_TEXT_FIELDS.map((name) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className={FULL_WIDTH.has(name) ? 'sm:col-span-2' : undefined}>
                      <FormLabel>{t(`customers:addresses.fields.${name}`)}</FormLabel>
                      <FormControl>
                        <Input
                          autoComplete="off"
                          type={name === 'phone' ? 'tel' : 'text'}
                          maxLength={MAX_LENGTH[name]}
                          className={name === 'countryCode' ? 'uppercase' : undefined}
                          {...field}
                        />
                      </FormControl>
                      {name === 'countryCode' || name === 'region' || name === 'phone' ? (
                        <FormDescription>{t(`customers:addresses.hints.${name}`)}</FormDescription>
                      ) : null}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <div className="flex flex-col gap-3">
              {(['isDefaultShipping', 'isDefaultBilling'] as const).map((name) => (
                <FormField
                  key={name}
                  control={form.control}
                  name={name}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-2">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked === true)}
                        />
                      </FormControl>
                      <FormLabel className="font-normal">
                        {t(`customers:addresses.fields.${name}`)}
                      </FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? t('common:actions.saving') : t('common:actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
